import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { User } from '../models/User';
import {
    loadToken,
    login as authLogin,
    register as authRegister,
    logout as authLogout,
    me as authMe,
    type Credentials,
    type RegisterPayload,
} from '../services/auth';

export type AuthBotPayload = {
    turnstile_token?: string;
    company_website?: string;
};

export type UserContextValue = {
    user: User | null;
    isLoading: boolean;
    login: (email: string, password: string, bot?: AuthBotPayload) => Promise<void>;
    register: (
        name: string,
        email: string,
        password: string,
        countryId?: number | null,
        bot?: AuthBotPayload
    ) => Promise<void>;
    logout: () => Promise<void>;
    refresh: () => Promise<void>;
};

const UserContext = createContext<UserContextValue | undefined>(undefined);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    useEffect(() => {
        const token = loadToken();
        if (!token) {
            setIsLoading(false);
            return;
        }
        authMe()
            .then((u) => setUser(u))
            .finally(() => setIsLoading(false));
    }, []);

    const login = useCallback(async (email: string, password: string, bot?: AuthBotPayload) => {
        const payload: Credentials = { email, password, ...bot };
        const u = await authLogin(payload);
        setUser(u);
    }, []);

    const register = useCallback(async (
        name: string,
        email: string,
        password: string,
        countryId?: number | null,
        bot?: AuthBotPayload
    ) => {
        const payload: RegisterPayload = {
            name,
            email,
            password,
            password_confirmation: password,
            country_id: countryId ?? undefined,
            ...bot,
        };
        const u = await authRegister(payload);
        setUser(u);
    }, []);

    const logout = useCallback(async () => {
        await authLogout();
        setUser(null);
    }, []);

    const refresh = useCallback(async () => {
        const u = await authMe();
        setUser(u);
    }, []);

    const value = useMemo(
        () => ({ user, isLoading, login, register, logout, refresh }),
        [user, isLoading, login, register, logout, refresh]
    );

    return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export function useUser(): UserContextValue {
    const ctx = useContext(UserContext);
    if (!ctx) throw new Error('useUser must be used within UserProvider');
    return ctx;
}
